"use client";

import Button from "@/components/Button";
import { Menu, X } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useEffect, useState } from "react";

export default function Header() {
	const [isMenuOpen, setIsMenuOpen] = useState(false);
	const [isVisible, setIsVisible] = useState(true);
	const [lastScrollY, setLastScrollY] = useState(0);
	const pathname = usePathname();

	const navLinks = [
		{ href: "/", label: "Home" },
		{ href: "/about", label: "About" },
		{ href: "/programs", label: "Programs" },
		{ href: "/registration", label: "Registration" },
		{ href: "/blog", label: "Blog" },
	];

	useEffect(() => {
		const handleScroll = () => {
			const currentScrollY = window.scrollY;

			// Show header when scrolling up, hide when scrolling down
			if (currentScrollY < lastScrollY || currentScrollY < 10) {
				setIsVisible(true);
			} else if (currentScrollY > lastScrollY && currentScrollY > 80) {
				setIsVisible(false);
				setIsMenuOpen(false); // Close mobile menu when hiding
			}

			setLastScrollY(currentScrollY);
		};

		// Add scroll event listener
		window.addEventListener("scroll", handleScroll, { passive: true });

		// Cleanup
		return () => window.removeEventListener("scroll", handleScroll);
	}, [lastScrollY]);

	// Close mobile menu when route changes
	useEffect(() => {
		setIsMenuOpen(false);
	}, [pathname]);

	const isActive = (href: string) => {
		if (href === "/") {
			return pathname === "/";
		}
		return pathname.startsWith(href);
	};

	return (
		<>
			{/* Spacer to prevent content jump */}
			<div className="h-20" />

			<header
				className={`bg-white shadow-sm fixed top-0 left-0 right-0 z-50 transition-transform duration-300 ${
					isVisible ? "translate-y-0" : "-translate-y-full"
				}`}
			>
				<div className="container mx-auto px-4">
					<div className="flex items-center justify-between h-20">
						{/* Logo - Fixed */}
						<Link href="/" className="flex-shrink-0">
							<div className="relative w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center">
								<Image
									src={"/assets/logo.png"}
									alt={"Logo"}
									fill
									className="object-contain rounded-full"
								/>
							</div>
						</Link>

						{/* Desktop Navigation */}
						<nav className="hidden md:flex items-center space-x-8">
							{navLinks.map((link) => (
								<Link
									title={link.label}
									key={link.href}
									href={link.href}
									className={`font-medium transition-colors relative ${
										isActive(link.href)
											? "text-primary"
											: "text-gray-700 hover:text-primary"
									}`}
								>
									{link.label}
									{isActive(link.href) && (
										<span className="absolute -bottom-1 left-0 right-0 h-0.5 bg-primary rounded-full" />
									)}
								</Link>
							))}
						</nav>

						{/* Contact Button - Desktop */}
						<div className="hidden md:block">
							<Button href="/contact">Contact</Button>
						</div>

						{/* Mobile Menu Button */}
						<button
							onClick={() => setIsMenuOpen(!isMenuOpen)}
							className="md:hidden p-2"
							aria-label="Toggle menu"
						>
							{isMenuOpen ? (
								<X className="w-6 h-6 text-gray-700" />
							) : (
								<Menu className="w-6 h-6 text-gray-700" />
							)}
						</button>
					</div>

					{/* Mobile Navigation */}
					{isMenuOpen && (
						<nav className="md:hidden py-4 border-t">
							<div className="flex flex-col space-y-4">
								{navLinks.map((link) => (
									<Link
										key={link.href}
										href={link.href}
										className={`font-medium transition-colors py-2 ${
											isActive(link.href)
												? "text-primary border-l-4 border-primary pl-4"
												: "text-gray-700 hover:text-primary pl-4"
										}`}
										onClick={() => setIsMenuOpen(false)}
									>
										{link.label}
									</Link>
								))}
								<Button href="/contact" className="w-full">
									Contact
								</Button>
							</div>
						</nav>
					)}
				</div>
			</header>
		</>
	);
}
